/**
 * What a transport error MEANS, asked once here rather than by every screen.
 *
 * `ConnectError` and its `Code` enum are this package's business — it owns the
 * clients and the interceptors — so a console that wants to know "did the server
 * refuse this, or fail at it?" should not have to import the transport to find
 * out. `@foir/auth` builds its withheld-read classification on this
 * (`read-reach.ts`), and react-query's retry policy reads it directly.
 */

import { Code, ConnectError } from "@connectrpc/connect";
import type { Translatable, Translator } from '@foir/i18n/message';

/**
 * isPermissionDenied reports the server REFUSING a call, as distinct from the
 * network or the server failing at it.
 *
 * Two callers, and they want it for different reasons:
 *
 *   - a list that must say "you cannot see these" rather than "there are none";
 *   - `retry`, because a refusal is not transient. The workspace's auth clients screen
 *     retried a 403 four times against a decision that was never going to
 *     change, then rendered "0 auth clients" from the wreckage.
 */
export function isPermissionDenied(error: unknown): boolean {
  return error instanceof ConnectError && error.code === Code.PermissionDenied;
}

/**
 * isUnauthenticated reports a call rejected for want of a session, which the
 * shell handles by sending the reader to sign in rather than by rendering
 * anything. Kept beside its sibling so the two are read together.
 */
export function isUnauthenticated(error: unknown): boolean {
  return error instanceof ConnectError && error.code === Code.Unauthenticated;
}

/**
 * isTransient reports a call that failed for a reason that passes: the caller
 * asked faster than it may, the platform could not be reached, or it ran out
 * of time. Asking again later can succeed; asking again after a refusal cannot.
 */
export function isTransient(error: unknown): boolean {
  return (
    error instanceof ConnectError &&
    (error.code === Code.ResourceExhausted ||
      error.code === Code.Unavailable ||
      error.code === Code.DeadlineExceeded)
  );
}

/**
 * retryAfterSeconds is how long a refusal over a ceiling said to wait
 * (Retry-After), or undefined when it said nothing.
 */
export function retryAfterSeconds(error: unknown): number | undefined {
  if (!(error instanceof ConnectError)) return undefined;
  const secs = Number(error.metadata.get("retry-after") ?? "");
  return Number.isFinite(secs) && secs > 0 ? secs : undefined;
}

/**
 * Codes whose server message is never fit to show a reader.
 *
 * The platform's convention is that the original error text flows through so
 * client logs keep their query context — `handlerconnect` says so in as many
 * words, and it maps anything unrecognised to Internal WITH the original
 * message. That is right for logs and wrong for a screen: it is how
 * "GetConfigByID: no rows in result set" reaches a user.
 *
 * Every other code is a decision the server made deliberately, and its message
 * is usually the most useful thing anyone can say — "workspace creation is
 * currently closed" beats any sentence written here. Those pass through.
 */
const OPAQUE_CODES = new Set<Code>([
  Code.Internal,
  Code.Unknown,
  Code.Unavailable,
  Code.DataLoss,
  Code.ResourceExhausted,
  Code.DeadlineExceeded,
]);

/**
 * What to put in front of a reader when a call fails.
 *
 * `action` is what the reader was trying to do, in their terms — "Couldn't
 * revoke access", "Couldn't start checkout". It is not optional, because the
 * one thing every one of these messages needs is to say which thing failed, and
 * a helper that let you omit it would be used without it.
 *
 * # Why not just `error.message`
 *
 * On a `ConnectError` that property is `"[code] rawMessage"` — the wire code is
 * IN the string. 287 call sites interpolated it directly, so the console showed
 * users `[permission_denied] workspace creation is currently closed` and
 * `Failed to revoke access: [not_found] access record not found`. Only five
 * sites in the repo had ever reached for `rawMessage`.
 *
 * # Why this is not an interceptor
 *
 * Normalising centrally at the transport would have to return something, and
 * anything it returns is either still a ConnectError (so nothing is fixed) or is
 * not (so `isPermissionDenied` above stops matching, and with it the whole
 * withheld-read classification, the retry policy's `error.code`, the plan-limit
 * reader's `error.metadata`, and `findDetails`). The error object has to survive
 * intact; it is only its RENDERING that must be disciplined, so the helper lives
 * where the rendering happens.
 */
export function userFacingMessage(
  error: unknown,
  action: Translatable,
  t: Translator,
): string {
  const reason = userFacingReason(error);
  // The GLUE is copy too. Each shape is a whole sentence rather than an action
  // with punctuation appended, because where the reason goes relative to the
  // action is a question every language answers for itself.
  const label = t(action);
  if (reason === null) {
    return t('common.action_failed_try_again', '{action}. Please try again.', {
      action: label,
    });
  }
  return reason
    ? t('common.action_failed_with_reason', '{action}: {reason}', { action: label, reason })
    : t('common.action_failed', '{action}.', { action: label });
}

/**
 * The part of a failure that can be shown on its own, with no action to attach
 * it to — `null` when there is nothing showable.
 *
 * This exists for one caller: the global mutation fallback, which fires for
 * mutations that declare no `onError` and so cannot know what was being
 * attempted. Everywhere else knows, and should say, which is why
 * `userFacingMessage` demands it.
 *
 * An empty string is not the same as `null`: it means the server refused and
 * said nothing, which is showable as "that didn't work" rather than as a
 * transport failure.
 */
export function userFacingReason(error: unknown): string | null {
  if (!(error instanceof ConnectError)) return null;
  if (OPAQUE_CODES.has(error.code)) return null;
  return error.rawMessage.trim();
}
